// Printable recipes grouped by production station (Tumbler, Caramelizer,
// Kettle): each station starts on a new page, with a checkbox and an
// ingredient table per recipe. Either one batch of every recipe, or the
// recipes in the production plan scaled to its batches. Percentage blends
// (just other flavors mixed together) don't get a card; the flavors they're
// mixed from show how much goes into them. Black-and-white US Letter, hand-built like the other
// *Pdf.ts helpers.

import { formatAmount } from './format'
import { STATIONS, type Station } from './stations'
import {
  LINE_H,
  MARGIN,
  PAGE_H,
  Pages,
  RIGHT,
  buildPdf,
  formatDay,
  line,
  strokeRect,
  text,
  truncate,
  weight,
  wrap,
} from './productionPlanPdf'

export interface StationRecipeLine {
  kind: 'ingredient' | 'flavor'
  name: string
  quantity: number | null
  unit: string | null
}

export interface StationRecipe {
  label_name: string
  station: string | null
  base_batch_weight_oz: number | null
  blend_by_percent: boolean
  recipe_only: boolean
  used_in: string[]
  lines: StationRecipeLine[]
}

/** The parts of a production plan flavor the printout scales by. */
export interface StationPlanFlavor {
  label_name: string
  has_recipe: boolean
  oz_to_make: number
  batches: number | null
  blend_oz?: number
  used_in?: string[]
}

export interface StationPlan {
  includeForecast: boolean
  forecast: { weekStart: string; weekEnd: string } | null
  forecastStale?: boolean
  flavors: StationPlanFlavor[]
}

/** A station, or null for recipes without one. */
export type StationChoice = Station | null

export interface StationRecipesOptions {
  recipes: StationRecipe[]
  /** Stations to print, in any order; null prints recipes with no station. */
  stations: StationChoice[]
  /** With a plan, only its flavors print, scaled to its batches. */
  plan?: StationPlan | null
}

const X_BOX = MARGIN + 2
const X_NAME = MARGIN + 18
const X_ING = MARGIN + 22
const X_PER_BATCH = MARGIN + 300
const X_TOTAL = MARGIN + 410

function stationTitle(s: StationChoice): string {
  return s ?? 'No station set'
}

/** Recipes for one station, alphabetical. */
function recipesFor(recipes: StationRecipe[], s: StationChoice): StationRecipe[] {
  return recipes
    .filter(r => (r.station ?? null) === s)
    .sort((a, b) => a.label_name.localeCompare(b.label_name, undefined, { numeric: true }))
}

function lineName(l: StationRecipeLine, stationOf: Map<string, string | null>): string {
  if (l.kind !== 'flavor') return l.name
  const st = stationOf.get(l.name)
  return `${l.name} (flavor${st ? `, ${st}` : ''})`
}

function perBatch(l: StationRecipeLine): string {
  if (l.quantity == null) return '-'
  return formatAmount(l.quantity, l.unit)
}

/** Total for the plan, or null when it can't be scaled. */
function total(l: StationRecipeLine, f: StationPlanFlavor): string | null {
  if (l.quantity == null) return null
  if (f.batches == null) return null
  return formatAmount(l.quantity * f.batches, l.unit)
}

function notes(r: StationRecipe, f: StationPlanFlavor | undefined): string[] {
  const out: string[] = []
  if (f && (f.blend_oz ?? 0) > 0 && (f.used_in ?? []).length > 0) {
    out.push(...wrap(`Includes ${weight(f.blend_oz ?? 0)} for ${(f.used_in ?? []).join(', ')}`, 80))
  } else if (r.used_in.length > 0) {
    out.push(...wrap(`${r.recipe_only ? 'Recipe-only, goes' : 'Also goes'} into ${r.used_in.join(', ')}`, 80))
  }
  if (f && f.batches == null) {
    out.push('No batch weight on this recipe, so only per-batch amounts are shown')
  }
  return out
}

/**
 * Recipes that get a card: ones with lines, minus percentage blends; with a
 * plan, only the flavors it makes.
 */
export function printableRecipes(recipes: StationRecipe[], plan: StationPlan | null): StationRecipe[] {
  const planned = plan ? new Set(plan.flavors.filter(f => f.has_recipe).map(f => f.label_name)) : null
  return recipes.filter(
    r => r.lines.length > 0 && !r.blend_by_percent && (!planned || planned.has(r.label_name)),
  )
}

/** Builds the recipes-by-station PDF and opens it in a new tab. */
export function generateStationRecipesPdf({ recipes, stations, plan }: StationRecipesOptions): void {
  const planBy = new Map((plan?.flavors ?? []).map(f => [f.label_name, f]))
  const stationOf = new Map(recipes.map(r => [r.label_name, r.station]))
  const printable = printableRecipes(recipes, plan ?? null)

  const order: StationChoice[] = [...STATIONS, null]
  const chosen = order.filter(s => stations.includes(s))
  const groups = chosen
    .map(s => ({ station: s, recipes: recipesFor(printable, s) }))
    .filter(g => g.recipes.length > 0)

  const generatedAt = new Date().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
  const basis = plan
    ? plan.includeForecast && plan.forecast && !plan.forecastStale
      ? `For the production plan: open orders not printed yet plus next week's forecast (${formatDay(plan.forecast.weekStart)} - ${formatDay(plan.forecast.weekEnd)}), minus stock`
      : 'For the production plan: open orders not printed yet, minus stock'
    : 'One batch of each recipe'

  const doc = new Pages()

  if (groups.length === 0) {
    doc.push(text(MARGIN, doc.top - 18, 20, 'F2', 'Recipes by Station'))
    doc.push(text(MARGIN, doc.top - 36, 10, 'F1', basis))
    doc.push(
      text(
        MARGIN,
        doc.top - 60,
        11,
        'F1',
        plan
          ? 'No recipes in the plan for the chosen stations.'
          : 'No recipes for the chosen stations.',
      ),
    )
    doc.flush()
  }

  for (const group of groups) {
    const title = stationTitle(group.station)
    const count = group.recipes.length

    // ── Station header (first page of each station) ──────────────────────────
    doc.push(text(MARGIN, doc.top - 20, 22, 'F2', title))
    doc.push(text(MARGIN, doc.top - 38, 10, 'F1', basis))
    doc.push(
      text(
        MARGIN,
        doc.top - 52,
        10,
        'F1',
        `${count} recipe${count === 1 ? '' : 's'} · printed ${generatedAt}`,
        0.3,
      ),
    )
    doc.top -= 64
    doc.push(line(MARGIN, doc.top, RIGHT, doc.top, 1.2))
    doc.top -= 6

    const continued = () => {
      doc.push(text(MARGIN, doc.top - 12, 10, 'F2', `${title} (continued)`, 0.3))
      doc.top -= 20
      doc.push(line(MARGIN, doc.top, RIGHT, doc.top, 1.2))
      doc.top -= 6
    }

    for (const r of group.recipes) {
      const f = plan ? planBy.get(r.label_name) : undefined
      const showTotal = !!f && f.batches != null
      const recipeNotes = notes(r, f)
      const headerH = 24 + recipeNotes.length * LINE_H + 16
      const rowH = 14
      const blockH = headerH + r.lines.length * rowH + 14

      // Keep a recipe on one page when it fits on a page at all.
      const usable = PAGE_H - 2 * MARGIN - 30
      if (doc.ensure(Math.min(blockH, usable))) continued()

      const drawColumns = () => {
        const y = doc.top - 10
        doc.push(text(X_ING, y, 8.5, 'F2', 'Ingredient', 0.3))
        doc.push(text(X_PER_BATCH, y, 8.5, 'F2', 'Per batch', 0.3))
        if (showTotal) {
          doc.push(text(X_TOTAL, y, 8.5, 'F2', 'Total', 0.3))
        }
        doc.top -= 14
        doc.push(line(X_ING, doc.top + 1, RIGHT, doc.top + 1, 0.5, 0.6))
      }

      // ── Recipe title ──────────────────────────────────────────────────────
      const y = doc.top - 16
      doc.push(strokeRect(X_BOX, y - 1, 10, 10, 0.8))
      doc.push(text(X_NAME, y, 13, 'F2', truncate(r.label_name, 40)))
      let right = ''
      let rightSub = ''
      if (f && f.batches != null) {
        right = `${f.batches} batch${f.batches === 1 ? '' : 'es'}`
        rightSub =
          r.base_batch_weight_oz != null
            ? `of ${weight(r.base_batch_weight_oz)} · ${weight(f.batches * r.base_batch_weight_oz)} total`
            : ''
      } else if (r.base_batch_weight_oz != null) {
        right = `Batch: ${weight(r.base_batch_weight_oz)}`
      }
      if (right) doc.push(text(X_TOTAL, y, 12, 'F2', right))
      if (rightSub) doc.push(text(X_TOTAL, y - 11, 8, 'F1', rightSub, 0.3))
      recipeNotes.forEach((n, i) => doc.push(text(X_NAME, y - 13 - i * LINE_H, 8.5, 'F1', n, 0.3)))
      doc.top -= 24 + recipeNotes.length * LINE_H + (rightSub && recipeNotes.length === 0 ? 4 : 0)

      // ── Ingredient table ──────────────────────────────────────────────────
      drawColumns()
      for (const l of r.lines) {
        if (doc.ensure(rowH)) {
          continued()
          doc.push(text(X_NAME, doc.top - 12, 10, 'F2', `${truncate(r.label_name, 40)} (continued)`))
          doc.top -= 16
          drawColumns()
        }
        const ly = doc.top - 10
        doc.push(text(X_ING, ly, 10, 'F1', truncate(lineName(l, stationOf), 50)))
        doc.push(text(X_PER_BATCH, ly, 10, showTotal ? 'F1' : 'F2', perBatch(l)))
        if (showTotal && f) {
          const t = total(l, f)
          if (t) doc.push(text(X_TOTAL, ly, 10, 'F2', t))
        }
        doc.top -= rowH
        doc.push(line(X_ING, doc.top + 1, RIGHT, doc.top + 1, 0.3, 0.8))
      }
      doc.top -= 14
    }

    doc.flush()
  }

  // ── Emit & open in a new tab ───────────────────────────────────────────────
  const bytes = buildPdf(doc.pages)
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener,noreferrer')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
